import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Plus } from "react-feather";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { DropdownItem } from "reactstrap";
import CustomSearchFilter from "../../Common/Component/CustomSearchFilter";
import ActionOptions from "../../Common/Component/ActionOptions";
import StatusBadge from "../../Common/Component/StatusBadge";
import { showAlert } from "../../../Services/Shared.service";
import { getTollsByCity, deleteToll } from "../../../Services/ServiceCity.service";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import TollForm from "./TollForm";
import DeleteConfirmDialog from "../../Common/Component/DeleteConfirmDialog";
import { Spinner } from "../../../AbstractElements";

const TollTab = ({ cityId }) => {
  const [tollData, setTollData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchData, setSearchData] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal and Dialog States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingToll, setEditingToll] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tollToDelete, setTollToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTolls = () => {
    setIsLoading(true);
    getTollsByCity(cityId)
      .then((res) => {
        if (res?.data?.data) {
          setTollData(res.data.data);
        } else {
          setTollData([]);
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || "Failed to fetch tolls";
        showAlert("error", errorMsg);
        setTollData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSaveToll = (savedToll, isEdit) => {
    setTollData((prev) => {
      if (isEdit) {
        return prev.map((item) =>
          item.suburbId === savedToll.suburbId ? savedToll : item
        );
      } else {
        return [savedToll, ...prev];
      }
    });
  };

  useEffect(() => {
    if (cityId) {
      fetchTolls();
    }
  }, [cityId]);

  const filteredData = useMemo(() => {
    if (!searchData) return tollData;
    const query = searchData.toLowerCase();
    return tollData.filter(
      (item) =>
        (item.tollName && item.tollName.toLowerCase().includes(query)) ||
        (item.charge && item.charge.toString().includes(query))
    );
  }, [tollData, searchData]);

  useEffect(() => {
    setPage(0);
  }, [searchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddClick = () => {
    setEditingToll(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row) => {
    setEditingToll(row);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setTollToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!tollToDelete) return;
    setIsDeleting(true);
    const actionType = tollToDelete.isActive ? "delete" : "recover";
    deleteToll(cityId, tollToDelete.suburbId)
      .then(() => {
        showAlert("success", `Toll ${actionType === "delete" ? "deleted" : "recovered"} successfully`);
        setDeleteDialogOpen(false);
        // Toggle the active status of the deleted/recovered toll locally
        setTollData((prev) =>
          prev.map((item) =>
            item.suburbId === tollToDelete.suburbId
              ? { ...item, isActive: !item.isActive }
              : item
          )
        );
        setTollToDelete(null);
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || `Failed to ${actionType} toll`;
        showAlert("error", errorMsg);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <Box sx={{ marginTop: "30px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "15px",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "700", color: "#313131" }}>
          Toll Gates
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Box sx={{ minWidth: "250px" }}>
            <CustomSearchFilter
              searchData={searchData}
              setSearchData={setSearchData}
              placeholder="Search Toll Gates"
            />
          </Box>
          <Box
            style={{
              color: "rgba(57, 181, 74, 1)",
              border: "1px solid rgba(57, 181, 74, 1)",
              borderRadius: "10px",
              backgroundColor: "rgba(225, 244, 228, 1)",
              height: "39px",
              minWidth: "140px",
              fontSize: "14px",
              padding: "0 20px",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: "600",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddClick}
          >
            <Plus size={18} />
            Add Toll Gate
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <div className="loader-box" style={{ height: "20vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner attrSpinner={{ className: "loader-3" }} />
        </div>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "0 4px 10px 1px rgba(8, 8, 8, 0.15)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      color: "#626262",
                      fontWeight: "bold",
                      fontSize: "14px",
                      backgroundColor: "#E1F4E4",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Gate Name</TableCell>
                  <TableCell>Toll Charge</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No Toll Gates found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow
                      key={row.suburbId}
                      sx={{
                        "& td": {
                          borderBottom: "none",
                          fontSize: "14px",
                          color: "#313131",
                        },
                      }}
                    >
                      <TableCell style={{ fontWeight: "600" }}>{row.tollName}</TableCell>
                      <TableCell>₹{parseFloat(row.charge || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <StatusBadge isDeleted={!row.isActive} />
                      </TableCell>
                      <TableCell align="center">
                        <ActionOptions key={row.suburbId}>
                          <Fragment>
                            <DropdownItem
                              className="border-0"
                              onClick={() => handleDeleteClick(row)}
                            >
                              {row.isActive ? (
                                <Fragment>
                                  <i className="fa fa-trash-o" /> Delete
                                </Fragment>
                              ) : (
                                <Fragment>
                                  <i className="fa fa-recycle" /> Recover
                                </Fragment>
                              )}
                            </DropdownItem>
                            <DropdownItem
                              className="border-0"
                              onClick={() => handleEditClick(row)}
                            >
                              <i className="fa fa-pencil" /> Edit
                            </DropdownItem>
                          </Fragment>
                        </ActionOptions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                textAlign: "center",
                overflowX: "hidden",
              },
              "& .MuiTablePagination-select": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "6px",
                border: "1px solid #D9D9D9",
              },
              "& .MuiTablePagination-actions button": {
                color: "rgba(112, 112, 112, 1)",
                transform: "scale(1.3)",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: "rgba(98, 98, 98, 1)",
                fontSize: "14px",
                textAlign: "center",
                m: "auto",
                letterSpacing: "0.02vw",
              },
            }}
          />
        </>
      )}

      {/* Toll Form Modal */}
      <TariffModal
        isOpen={isModalOpen}
        size="lg"
        centered
        toggler={() => setIsModalOpen(false)}
        title={editingToll ? "Edit Toll" : "Add Toll Gate"}
      >
        <TollForm
          cityId={cityId}
          editingToll={editingToll}
          toggleModal={() => setIsModalOpen(false)}
          onTollSaved={handleSaveToll}
          fetchTolls={fetchTolls}
        />
      </TariffModal>

      {/* Delete/Recover Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title={tollToDelete?.isActive ? "Delete Toll Gate" : "Recover Toll Gate"}
        message={`Are you sure you want to ${tollToDelete?.isActive ? "delete" : "recover"} the toll gate "${tollToDelete?.tollName}"?`}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default TollTab;
